import { TitleComponent } from "Components/Title";
import styles from "./index.module.css";
import { CryptoInfo } from "./CryptoInfo";
import c2cToken from "assets/svg/c2c-token.svg";
import { InfoComponent } from "Components/InfoComponent";
import { Button, Divider, Spin, Tooltip } from "antd";
import ton from "assets/svg/tonkeeper.svg";
import infoRed from "assets/svg/infoRed.svg";
import { CustomButton } from "Components/CustomButton";
import xCircle from "assets/svg/XCircle.svg";
import sortIcon from "assets/svg/SortAscending.svg";
import { TableAntdV2 } from "Components/TableV2";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { UserNameRatingPhotoComponent } from "Components/UserNameRatingPhotoComponent";
import { CustomPagination } from "Components/CustomPagination";
import { useRequest } from "ahooks";
import {
  getCryptoFlowBase,
  getCryptoFlowMetrics,
  getCryptoFlowTransactions,
  SortByUserPayments,
} from "helpers/backend_helper";
import { AdminContext } from "context/adminContext";
import { NotificationContext } from "context/notificationContext";
import dayjs from "dayjs";
import { Pagination } from "../Options";
import { useSearchParams } from "react-router-dom";
import { PROP_NAME_SKIP_ADS, PROP_NAME_SORTBY } from "helpers/constants";
import { onSort } from "helpers/helper";
import { formatMoney, formatNumber, formatPercent } from "helpers/format";

const MAX_PAGES = 10;
export const CtyproFlow = () => {
  const { localCountry } = useContext(AdminContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [pagination, setPagination] = useState<Pagination>({ skip: 0, take: MAX_PAGES, total: 0 });
  const [searchParams, setSearchParams] = useSearchParams();
  const [skipAds, setSkipAds] = useState(0);
  const skipAdsFilter = searchParams.get(PROP_NAME_SKIP_ADS);
  const sortByFilter = searchParams.get(PROP_NAME_SORTBY);

  const sortBy = (sortByFilter as SortByUserPayments) || "";

  useEffect(() => {
    if (skipAdsFilter) {
      setSkipAds(+skipAdsFilter);
    } else {
      setSkipAds(0);
    }
  }, [skipAdsFilter]);

  const { data: cryptoBase, loading: isCryptoBaseLoading } = useRequest(
    () => getCryptoFlowBase(localCountry?.iso?.toUpperCase()),
    {
      refreshDeps: [localCountry],
      ready: !!localCountry,
      debounceWait: 300,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
      },
    },
  );

  const { data: cryptoMetrics, loading: isCryptoMetricsLoading } = useRequest(
    () => getCryptoFlowMetrics(localCountry?.iso?.toUpperCase()),
    {
      refreshDeps: [localCountry],
      ready: !!localCountry,
      debounceWait: 300,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
      },
    },
  );

  const [choosenDateFrom, setChoosenDateFrom] = useState("");

  const { data: cryptoTransactions, loading: isCryptoTransactionsLoading } = useRequest(
    () =>
      getCryptoFlowTransactions(
        { skip: skipAds, take: MAX_PAGES, total: pagination.total },
        localCountry?.iso?.toUpperCase(),
        sortBy,
        choosenDateFrom ? new Date(choosenDateFrom).toISOString() : null,
      ),
    {
      refreshDeps: [localCountry, skipAds, sortBy, choosenDateFrom],
      ready: !!localCountry,
      debounceWait: 300,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || "something went wrong");
      },
      onSuccess: (data) => {
        setPagination((prev) => ({ ...prev, total: +data.totalCount }));
      },
    },
  );

  const onSortBy = useCallback(
    (sortName) => {
      onSort(sortName, searchParams, sortBy, setSearchParams);
    },
    [searchParams, setSearchParams, sortBy],
  );

  const columns = [
    {
      title: (
        <div className={styles.columnSort}>
          <div>Date</div>
          <Button
            type="text"
            icon={
              <img
                src={sortIcon}
                alt="usagePercentage"
                style={{ width: 16, height: 16, transform: sortBy === "payout_date" ? "rotate(180deg)" : null }}
              />
            }
            onClick={() => {
              onSortBy("payout_date");
            }}
          />
        </div>
      ),
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Deal ID",
      dataIndex: "dealId",
      key: "dealId",
    },
    {
      title: "User",
      dataIndex: "user",
      key: "user",
    },
    {
      title: (
        <div className={styles.columnSort}>
          <div>Amount</div>
          <Button
            type="text"
            icon={
              <img
                src={sortIcon}
                alt="usagePercentage"
                style={{ width: 16, height: 16, transform: sortBy === "amount" ? "rotate(180deg)" : null }}
              />
            }
            onClick={() => {
              onSortBy("amount");
            }}
          />
        </div>
      ),
      dataIndex: "amount",
      key: "amount",
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
    },
  ];

  const dataSource = useMemo(() => {
    if (cryptoTransactions?.transactions.length) {
      return cryptoTransactions.transactions.map((tr) => ({
        key: tr.trId || tr.rentId,
        date: (
          <div
            className={`${
              tr.paymentCurrency === "TOKEN"
                ? +tr.balanceTon < 0
                  ? styles.redDate
                  : ""
                : +tr.balance < 0
                ? styles.redDate
                : ""
            }`}
          >
            {dayjs(new Date(tr.plannedPayoutDate)).format("DD MMM")}
          </div>
        ),
        dealId: `...${tr.rentId.slice(-4)}`,
        user: (
          <UserNameRatingPhotoComponent
            el={{
              avatar: tr.lessor.avatar,
              firstName: tr.lessor.name,
              lastName: "",
              rating: tr.lessor.rating || 0,
              isOnlyName: true,
              online: tr.lessor.online,
              lastOnline: tr.lessor.lastOnline,
              reviewCount: tr.lessor.review_count,
            }}
          />
        ),
        amount:
          tr.paymentCurrency === "TOKEN" ? (
            <div className={styles.balance}>
              <img src={c2cToken} alt="ton" width={12} height={14} style={{ marginRight: 2 }} />
              {formatNumber(tr.amount)}
            </div>
          ) : (
            formatMoney(tr.amount)
          ),
        balance:
          tr.paymentCurrency === "TOKEN" ? (
            <div className={styles.balance}>
              <img src={c2cToken} alt="ton" width={12} height={14} style={{ marginRight: 2 }} />
              {formatNumber(Math.abs(Number(tr.balance)))}
              {tr.balanceTon < 0 ? (
                <Tooltip title="Insufficient Ton Balance">
                  <img src={infoRed} alt="info" width={13} height={13} style={{ marginLeft: 9.5 }} />
                </Tooltip>
              ) : null}
            </div>
          ) : (
            <div className={`${styles.balance} ${+tr.balance < 0 ? styles.balanceMinus : ""}`}>
              {+tr.balance > 0 ? formatMoney(tr.balance) : `- ${formatMoney(Math.abs(Number(tr.balance)))}`}
            </div>
          ),
      }));
    }

    return [];
  }, [cryptoTransactions]);
  return (
    <div className={styles.container}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <TitleComponent text="Crypto Flow" />
      </div>
      <div className={styles.infoWrapper}>
        <CryptoInfo
          isCryptoBaseLoading={isCryptoBaseLoading}
          balance_eur={formatNumber(cryptoBase?.balance_eur || 0)}
          balance_tokens={formatNumber(cryptoBase?.balance_tokens || 0)}
          balance_tons={formatNumber(cryptoBase?.balance_tons || 0)}
          iban={cryptoBase?.iban}
          wallet={cryptoBase?.wallet}
        />
        <div className={styles.infoPiecesWrapper}>
          {[
            {
              infoOfData: "Issues to the service",
              label: "Issue",
              value: (
                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                  <img src={c2cToken} alt="c2cToken" style={{ width: 17, height: 20 }} />
                  <span>{formatNumber(cryptoBase?.issue || 0)}</span>
                </div>
              ),
              isLoading: isCryptoBaseLoading,
            },
            {
              infoOfData: "in use by users",
              label: "In use",
              value: formatPercent(cryptoBase?.inUse || 0),
              isLoading: false,
              moreInfo: {
                value: `${
                  Number(cryptoBase?.inUseIndicator || 0) > 0
                    ? `+${formatNumber(cryptoBase?.inUseIndicator || 0)}%`
                    : formatPercent(cryptoBase?.inUseIndicator || 0)
                }`,
                isPositive: Number(cryptoBase?.inUseIndicator || 0) > 0,
              },
            },
            {
              infoOfData: "Delta between issues and in use",
              label: "Delta",
              value: formatPercent(cryptoBase?.deltaPercent || 0),
              isLoading: false,
              moreValue: {
                value: (
                  <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    <img src={c2cToken} alt="c2cToken" style={{ width: 17, height: 20 }} />
                    <span>{formatNumber(cryptoBase?.deltaTokens || 0)}</span>
                  </div>
                ),
              },
            },
          ].map(({ infoOfData, label, value, isLoading, moreInfo, moreValue }, i) => (
            <InfoComponent
              infoOfData={infoOfData}
              label={label}
              value={value}
              key={i}
              isLoading={isLoading}
              moreInfo={moreInfo}
              moreValue={moreValue}
            />
          ))}
        </div>
      </div>
      <Divider style={{ borderWidth: 2, margin: "20px" }} />
      <div className={styles.secondLineWrapper}>
        <div className={styles.bigInfoWrapper}>
          {[
            {
              infoOfData: "Insurance amount in c2c tokens and euros",
              label: "Amount",
              value: formatMoney(cryptoMetrics?.amount_eur || 0),
              isLoading: isCryptoMetricsLoading,
              isWarning: Number(cryptoMetrics?.amount_eur || 0) < 0,
              moreValue: [
                {
                  value: (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <img src={c2cToken} alt="c2cToken" />
                      <span style={{ lineHeight: "0" }}>{formatNumber(cryptoMetrics?.amount_tokens || 0)}</span>
                    </div>
                  ),
                  isNegative: Number(cryptoMetrics?.amount_tokens || 0) < 0,
                },
                {
                  value: (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <img src={ton} alt="ton" />
                      <span style={{ lineHeight: "0" }}>{formatNumber(cryptoMetrics?.gas_tons || 0)}</span>
                    </div>
                  ),
                  isNegative: Number(cryptoMetrics?.gas_tons || 0) < 0,
                },
              ],
            },
            {
              infoOfData: "Total Balance in c2c tokens and euros",
              label: "Total Balance",
              value: formatMoney(cryptoMetrics?.total_eur || 0),
              isLoading: false,
              isWarning: Number(cryptoMetrics?.total_eur || 0) < 0,
              moreValue: [
                {
                  value: (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <img src={c2cToken} alt="c2cToken" />
                      <span style={{ lineHeight: "0" }}>{formatNumber(cryptoMetrics?.total_tokens || 0)}</span>
                    </div>
                  ),
                  isNegative: Number(cryptoMetrics?.total_tokens || 0) < 0,
                },
                {
                  value: (
                    <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                      <img src={ton} alt="ton" />
                      <span style={{ lineHeight: "0" }}>{formatNumber(cryptoMetrics?.total_tons || 0)}</span>
                    </div>
                  ),
                  isNegative: Number(cryptoMetrics?.total_tons || 0) < 0,
                },
              ],
            },
          ].map(({ infoOfData, label, value, isLoading, moreValue, isWarning }, i) => (
            <InfoComponent
              infoOfData={infoOfData}
              label={label}
              value={value}
              key={i}
              isLoading={isLoading}
              moreValue={moreValue}
              isWarning={isWarning}
            />
          ))}
        </div>
        {isCryptoMetricsLoading ? (
          <div style={{ width: "100%", marginTop: 20 }}>
            <Spin style={{ top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />
          </div>
        ) : (
          cryptoMetrics?.liquidity_exhaustion_date && (
            <div style={{ display: "flex", alignItems: "end", gap: 10 }}>
              <div className={styles.jumpToDateWrapper}>
                <div>{dayjs(new Date(cryptoMetrics.liquidity_exhaustion_date)).format("DD.MM.YYYY")}</div>
                <CustomButton
                  sizeButton="medium"
                  typeButton="secondary"
                  onClick={() => {
                    setChoosenDateFrom(cryptoMetrics?.liquidity_exhaustion_date);
                  }}
                >
                  <div>Jump to Date</div>
                </CustomButton>
              </div>
              {choosenDateFrom ? (
                <CustomButton
                  sizeButton="medium"
                  typeButton="tertiary"
                  onClick={() => {
                    setChoosenDateFrom(null);
                  }}
                  className={styles.resetButton}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <img src={xCircle} alt="reset" />
                    <span>Reset</span>
                  </div>
                </CustomButton>
              ) : null}
            </div>
          )
        )}
      </div>
      <div style={{ marginTop: 20 }}>
        <TableAntdV2
          columns={columns}
          dataSource={dataSource}
          className={styles.table}
          loading={isCryptoTransactionsLoading}
        />
        <div style={{ marginTop: 40 }}>
          <CustomPagination
            total={pagination.total}
            current={Math.floor(skipAds / MAX_PAGES + 1)}
            defaultPageSize={MAX_PAGES}
            hideOnSinglePage
            showSizeChanger={false}
            onChange={(page) => {
              const pageMinus = page - 1;
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set(PROP_NAME_SKIP_ADS, (pageMinus * MAX_PAGES).toString());
              setSearchParams(newSearchParams, { replace: true });
            }}
          />
        </div>
      </div>
    </div>
  );
};
